import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Car, ChevronsUpDown, Check, Download, FileSpreadsheet, File, FileType, FileJson, FileText } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../../components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { DialogoFechaCaja } from "../../components/DialogoFechaCaja";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

const API = process.env.REACT_APP_BACKEND_URL;

const VehiculosCalculo = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [calculos, setCalculos] = useState([]);
  const [fechaCajaDialogOpen, setFechaCajaDialogOpen] = useState(false);
  const [calculoParaEnviar, setCalculoParaEnviar] = useState(null);

  const esMesActual = (fecha) => {
    if (!fecha) return false;
    const hoy = new Date();
    const [y, m] = fecha.split("-");
    return parseInt(y) === hoy.getFullYear() && parseInt(m) === (hoy.getMonth() + 1);
  };
  const [calculoDialogOpen, setCalculoDialogOpen] = useState(false);
  const [editingCalculo, setEditingCalculo] = useState(null);
  const [calculoForm, setCalculoForm] = useState({});
  const [clasificadorVeh, setClasificadorVeh] = useState([]);
  const [oficinas, setOficinas] = useState([]);
  const [vehiculosContrib, setVehiculosContrib] = useState([]);
  const [rifPopoverOpen, setRifPopoverOpen] = useState(false);
  const [placaPopoverOpen, setPlacaPopoverOpen] = useState(false);
  const [contribConRegistro, setContribConRegistro] = useState([]);
  const [aniosPendientes, setAniosPendientes] = useState([]);
  const [aniosSeleccionados, setAniosSeleccionados] = useState([]);
  const [aniosEnCalculo, setAniosEnCalculo] = useState([]);
  const [cotizacionActiva, setCotizacionActiva] = useState(null);
  const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState(null);
  const [calcFilters, setCalcFilters] = useState({ nro: "", rif: "", nombre: "", placa: "", fecha: "", total: "", estatus_pago: "", estatus: "" });

  const fetchCalculos = async () => {
    try {
      const r = await axios.get(`${API}/api/vehiculos/calculo`, { headers });
      setCalculos(r.data);
    } catch { toast.error("Error al cargar cálculos de vehículos"); }
  };

  const fetchClasificadorVeh = async () => {
    try {
      const r = await axios.get(`${API}/api/vehiculos/clasificador`, { headers });
      setClasificadorVeh(r.data);
    } catch { setClasificadorVeh([]); }
  };

  const fetchOficinas = async () => {
    try {
      const r = await axios.get(`${API}/api/definiciones/oficinas`, { headers });
      setOficinas(r.data);
    } catch { setOficinas([]); }
  };

  const fetchVehiculosPorRif = async (rif) => {
    try {
      const r = await axios.get(`${API}/api/vehiculos/registro-por-rif/${rif}`, { headers });
      setVehiculosContrib(r.data);
    } catch { setVehiculosContrib([]); }
  };

  const fetchContribConRegistro = async () => {
    try {
      const r = await axios.get(`${API}/api/vehiculos/contribuyentes-con-registro`, { headers });
      setContribConRegistro(r.data);
    } catch { setContribConRegistro([]); }
  };

  const fetchCotizacionActiva = async () => {
    try {
      const r = await axios.get(`${API}/api/definiciones/cotizaciones`, { headers });
      const activa = r.data.find(c => c.estatus === "Activo");
      setCotizacionActiva(activa || null);
    } catch { setCotizacionActiva(null); }
  };

  useEffect(() => {
    fetchCalculos();
    fetchClasificadorVeh();
    fetchOficinas();
    fetchContribConRegistro();
    fetchCotizacionActiva();
  }, []);

  // Line helpers
  const addLinea = () => {
    const lineas = [...(calculoForm.lineas_impuestos || []), { tipo: "", ingreso_bruto: 0, importe: 0, sub_total: 0 }];
    setCalculoForm({ ...calculoForm, lineas_impuestos: lineas });
  };
  const removeLinea = (idx) => {
    const lineas = (calculoForm.lineas_impuestos || []).filter((_, i) => i !== idx);
    setCalculoForm({ ...calculoForm, lineas_impuestos: lineas });
  };
  const updateLinea = (idx, field, value) => {
    const lineas = [...(calculoForm.lineas_impuestos || [])];
    lineas[idx] = { ...lineas[idx], [field]: value };
    if (field === "tipo") {
      const clas = clasificadorVeh.find(c => c.tipo_clase_vehiculo === value);
      if (clas) lineas[idx].importe = clas.tasa || 0;
    }
    lineas[idx].sub_total = (lineas[idx].ingreso_bruto || 0) * (lineas[idx].importe || 0);
    setCalculoForm({ ...calculoForm, lineas_impuestos: lineas });
  };
  const calcSubTotal = () => (calculoForm.lineas_impuestos || []).reduce((s, l) => s + (l.sub_total || 0), 0);
  const calcTotal = () => {
    const sub = calcSubTotal();
    return sub - (calculoForm.descuento || 0) + (calculoForm.reintegro || 0) - (calculoForm.retencion || 0) + (calculoForm.multa || 0);
  };

  const openNewCalculo = async () => {
    setEditingCalculo(null);

    // Auto-montaje de tipos de impuesto anclados a Vehículos
    let anclados = [];
    try {
      const res = await axios.get(`${API}/api/definiciones/tipos-impuesto/anclados/vehiculos`, { headers });
      anclados = (res.data || []).map((ti) => ({
        tipo_impuesto_id: ti.id, tipo_impuesto_nombre: ti.nombre, concepto: ti.nombre,
        descripcion: ti.descripcion || ti.nombre, tasa: ti.tasa || 0,
        cantidad: 1, sub_total: 0,
        codigo_presupuestario_id: ti.codigo_presupuestario_id || null, _anclado: true,
      }));
    } catch (err) { console.warn("[anclados/vehiculos]", err); toast.warning("No se pudieron cargar los impuestos anclados"); }

    setCalculoForm({
      fecha: new Date().toISOString().split("T")[0],
      clase_contribuyente: "",
      lineas_impuestos: anclados,
      descuento: 0, reintegro: 0, retencion: 0, multa: 0, divisas_usd: 0, divisas_eur: 0
    });
    setVehiculosContrib([]);
    setAniosPendientes([]);
    setAniosSeleccionados([]);
    setAniosEnCalculo([]);
    setVehiculoSeleccionado(null);
    setCalculoDialogOpen(true);
  };

  const openEditCalculo = (item) => {
    setEditingCalculo(item);
    setCalculoForm({ ...item });
    if (item.rif) fetchVehiculosPorRif(item.rif);
    setCalculoDialogOpen(true);
  };

  const handleSaveCalculo = async () => {
    if (!calculoForm.rif) { toast.error("RIF / Contribuyente es obligatorio"); return; }
    if (!calculoForm.placa) { toast.error("Placa es obligatoria"); return; }
    try {
      if (editingCalculo) {
        const total = calcTotal();
        const divisas_usd = cotizacionActiva?.dolar > 0 ? parseFloat((total / cotizacionActiva.dolar).toFixed(2)) : 0;
        const divisas_eur = cotizacionActiva?.euro > 0 ? parseFloat((total / cotizacionActiva.euro).toFixed(2)) : 0;
        const payload = { ...calculoForm, total, divisas_usd, divisas_eur };
        await axios.put(`${API}/api/vehiculos/calculo/${editingCalculo.id}`, payload, { headers });
        toast.success("Cálculo de vehículo actualizado");
      } else {
        if (aniosSeleccionados.length === 0) { toast.error("Seleccione al menos un año a pagar"); return; }
        const total = calcTotal();
        const divisas_usd = cotizacionActiva?.dolar > 0 ? parseFloat((total / cotizacionActiva.dolar).toFixed(2)) : 0;
        const divisas_eur = cotizacionActiva?.euro > 0 ? parseFloat((total / cotizacionActiva.euro).toFixed(2)) : 0;
        const payload = {
          ...calculoForm,
          total, divisas_usd, divisas_eur,
          anio_a_pagar: Math.max(...aniosSeleccionados),
          anios_pagados: aniosSeleccionados
        };
        await axios.post(`${API}/api/vehiculos/calculo`, payload, { headers });
        toast.success("Cálculo de vehículo creado exitosamente");
      }
      setCalculoDialogOpen(false);
      fetchCalculos();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al guardar"); }
  };

  const handleEnviarCaja = async (id, actualizarFecha = false) => {
    try {
      const url = `${API}/api/vehiculos/calculo/${id}/enviar-caja${actualizarFecha ? '?actualizar_fecha=true' : ''}`;
      await axios.post(url, {}, { headers });
      toast.success("Cálculo enviado a caja exitosamente");
      fetchCalculos();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al enviar a caja"); }
  };

  const handleEnviarCajaClick = (item) => {
    if (!esMesActual(item.fecha)) {
      setCalculoParaEnviar(item);
      setFechaCajaDialogOpen(true);
    } else {
      handleEnviarCaja(item.id);
    }
  };

  const handleDeleteCalculo = async (id) => {
    try {
      await axios.delete(`${API}/api/vehiculos/calculo/${id}`, { headers });
      toast.success("Cálculo eliminado");
      fetchCalculos();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleAnularCalculo = async (id) => {
    try {
      await axios.put(`${API}/api/vehiculos/calculo/${id}`, { estatus: "Anulado" }, { headers });
      toast.success("Cálculo anulado exitosamente");
      fetchCalculos();
    } catch { toast.error("Error al anular"); }
  };

  const selectContribCalculo = (c) => {
    const rif = c.rif;
    setCalculoForm({ ...calculoForm, rif, nombre_razon_social: c.nombre_razon_social || "", clase_contribuyente: c.clase_contribuyente || "", placa: "", ultimo_anio_pago: null, anio_a_pagar: null });
    setRifPopoverOpen(false);
    setAniosPendientes([]);
    setAniosSeleccionados([]);
    setAniosEnCalculo([]);
    setVehiculoSeleccionado(null);
    fetchVehiculosPorRif(rif);
  };

  const selectPlaca = async (v) => {
    setPlacaPopoverOpen(false);
    setAniosSeleccionados([]);
    setVehiculoSeleccionado(v);
    try {
      const r = await axios.get(`${API}/api/vehiculos/siguiente-anio-pagar/${v.placa}`, { headers });
      const info = r.data;
      const pendientes = (info.anios_pendientes || []).filter(a => !(info.anios_en_calculo || []).includes(a));
      setAniosPendientes(pendientes);
      setAniosEnCalculo(info.anios_en_calculo || []);
      setCalculoForm({ ...calculoForm, placa: v.placa, ultimo_anio_pago: info.ultimo_anio_pago || null, anio_a_pagar: null, lineas_impuestos: [] });
      if (pendientes.length === 0) {
        toast.info(`El vehículo ${v.placa} está al día o ya tiene cálculos para todos los años pendientes.`);
      }
    } catch {
      setCalculoForm({ ...calculoForm, placa: v.placa, ultimo_anio_pago: v.ultimo_anio_pago || null, anio_a_pagar: null, lineas_impuestos: [] });
      setAniosPendientes([]);
      setAniosEnCalculo([]);
    }
  };

  const toggleAnio = (anio) => {
    setAniosSeleccionados(prev => {
      let nuevos;
      if (prev.includes(anio)) {
        nuevos = prev.filter(a => a < anio);
      } else {
        const set = new Set(prev);
        for (const a of aniosPendientes) {
          if (a <= anio) set.add(a);
        }
        nuevos = [...set].sort((a, b) => a - b);
      }
      const tipoVeh = vehiculoSeleccionado?.tipo_clasificacion || "";
      const clasMatch = clasificadorVeh.find(c => c.tipo_clase_vehiculo === tipoVeh);
      const tasa = clasMatch?.tasa || 0;
      const ingresoBruto = cotizacionActiva?.valor_unidad_cuenta || 0;
      const lineas = nuevos.map(a => ({
        tipo: `${tipoVeh} - ${a}`,
        ingreso_bruto: ingresoBruto,
        importe: tasa,
        sub_total: ingresoBruto * tasa,
        auto_generada: true
      }));
      setCalculoForm(f => ({ ...f, lineas_impuestos: lineas }));
      return nuevos;
    });
  };

  const filteredCalculos = calculos.filter(c => {
    const cf = calcFilters;
    if (cf.nro && !(c.nro_boletin_vehiculo || "").toLowerCase().includes(cf.nro.toLowerCase())) return false;
    if (cf.rif && !(c.rif || "").toLowerCase().includes(cf.rif.toLowerCase())) return false;
    if (cf.nombre && !(c.nombre_razon_social || "").toLowerCase().includes(cf.nombre.toLowerCase())) return false;
    if (cf.placa && !(c.placa || "").toLowerCase().includes(cf.placa.toLowerCase())) return false;
    if (cf.fecha && !(c.fecha || "").includes(cf.fecha)) return false;
    if (cf.total && !String(c.total || "").includes(cf.total)) return false;
    if (cf.estatus_pago && cf.estatus_pago !== "todos" && (c.estatus_pago || "Sin pagar") !== cf.estatus_pago) return false;
    if (cf.estatus && cf.estatus !== "todos" && (c.estatus || "Registrado") !== cf.estatus) return false;
    return true;
  });

  // ============ EXPORT ============
  const getExportData = () => filteredCalculos.map(item => ({
    "Nro.": item.nro_boletin_vehiculo || "",
    "RIF": item.rif || "",
    "Contribuyente": item.nombre_razon_social || "",
    "Fecha": item.fecha || "",
    "Total (Bs.)": (item.total || 0).toFixed(2),
    "Estatus Pago": item.estatus_pago || "Sin pagar",
    "Estatus": item.estatus || "Registrado"
  }));

  const exportToExcel = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const ws = XLSX.utils.json_to_sheet(d); const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Calculo Vehiculo"); XLSX.writeFile(wb, `calculo_vehiculo_${new Date().toISOString().split("T")[0]}.xlsx`); toast.success("Exportado a Excel exitosamente"); };
  const exportToPDF = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const doc = new jsPDF("landscape"); doc.setFontSize(16); doc.text("Cálculo de Vehículo", 14, 20); doc.setFontSize(10); doc.text(`Fecha: ${new Date().toLocaleDateString("es-VE")}`, 14, 28); autoTable(doc, { head: [Object.keys(d[0])], body: d.map(r => Object.values(r)), startY: 35, styles: { fontSize: 7 }, headStyles: { fillColor: [37, 99, 235] } }); doc.save(`calculo_vehiculo_${new Date().toISOString().split("T")[0]}.pdf`); toast.success("Exportado a PDF exitosamente"); };
  const exportToCSV = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const h = Object.keys(d[0]).join(","); const rows = d.map(r => Object.values(r).map(v => `"${v}"`).join(",")).join("\n"); saveAs(new Blob(["\ufeff" + h + "\n" + rows], { type: "text/csv;charset=utf-8" }), `calculo_vehiculo_${new Date().toISOString().split("T")[0]}.csv`); toast.success("Exportado a CSV exitosamente"); };
  const exportToText = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const hs = Object.keys(d[0]); const cw = hs.map(h => Math.max(h.length, ...d.map(r => String(r[h]).length))); let t = "CÁLCULO DE VEHÍCULO\n"; t += `Fecha: ${new Date().toLocaleDateString("es-VE")}\n`; t += "=".repeat(Math.max(cw.reduce((a, b) => a + b + 3, 0), 50)) + "\n\n"; t += hs.map((h, i) => h.padEnd(cw[i])).join(" | ") + "\n"; t += cw.map(w => "-".repeat(w)).join("-+-") + "\n"; d.forEach(r => { t += hs.map((h, i) => String(r[h]).padEnd(cw[i])).join(" | ") + "\n"; }); saveAs(new Blob([t], { type: "text/plain;charset=utf-8" }), `calculo_vehiculo_${new Date().toISOString().split("T")[0]}.txt`); toast.success("Exportado a Texto exitosamente"); };
  const exportToHTML = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } const hs = Object.keys(d[0]); const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Cálculo de Vehículo</title><style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#2563eb}table{border-collapse:collapse;width:100%;margin-top:20px}th,td{border:1px solid #ddd;padding:10px;text-align:left;font-size:12px}th{background-color:#2563eb;color:white}tr:nth-child(even){background-color:#f9fafb}tr:hover{background-color:#f3f4f6}.fecha{color:#6b7280;margin-bottom:20px}</style></head><body><h1>Cálculo de Vehículo</h1><p class="fecha">Fecha: ${new Date().toLocaleDateString("es-VE")}</p><table><thead><tr>${hs.map(h => `<th>${h}</th>`).join("")}</tr></thead><tbody>${d.map(r => `<tr>${Object.values(r).map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`; saveAs(new Blob([html], { type: "text/html;charset=utf-8" }), `calculo_vehiculo_${new Date().toISOString().split("T")[0]}.html`); toast.success("Exportado a HTML exitosamente"); };
  const exportToJSON = () => { const d = getExportData(); if (!d.length) { toast.error("No hay datos para exportar"); return; } saveAs(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" }), `calculo_vehiculo_${new Date().toISOString().split("T")[0]}.json`); toast.success("Exportado a JSON exitosamente"); };

  return (
    <div className="space-y-6" data-testid="calculo-vehiculo">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Cálculo de Vehículo</h1>
          <p className="text-slate-500">Gestión de cálculos de impuestos y tasas vehiculares</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredCalculos.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{calculos.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="btn-exportar-calc-veh"><Download className="w-4 h-4" /> Exportar</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToText} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> Texto</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToHTML} className="gap-2 cursor-pointer"><FileType className="w-4 h-4" /> HTML</DropdownMenuItem>
              <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer"><FileJson className="w-4 h-4" /> JSON</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openNewCalculo} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo-calculo-veh">
            <Plus className="w-4 h-4 mr-2" /> Nuevo
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Nro.</TableHead>
              <TableHead>RIF</TableHead>
              <TableHead>Contribuyente</TableHead>
              <TableHead>Placa</TableHead>
              <TableHead className="text-center">Año a Pagar</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-right">Total (Bs.)</TableHead>
              <TableHead className="text-center">Estatus Pago</TableHead>
              <TableHead className="text-center">Estatus</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
            <TableRow className="bg-slate-50/50 border-b">
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={calcFilters.nro} onChange={(e) => setCalcFilters({...calcFilters, nro: e.target.value})} data-testid="filter-calc-nro" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={calcFilters.rif} onChange={(e) => setCalcFilters({...calcFilters, rif: e.target.value})} data-testid="filter-calc-rif" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={calcFilters.nombre} onChange={(e) => setCalcFilters({...calcFilters, nombre: e.target.value})} data-testid="filter-calc-nombre" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={calcFilters.placa} onChange={(e) => setCalcFilters({...calcFilters, placa: e.target.value})} data-testid="filter-calc-placa" /></TableHead>
              <TableHead className="px-2 py-1.5"></TableHead>
              <TableHead className="px-2 py-1.5"><Input type="date" className="h-7 text-xs" value={calcFilters.fecha} onChange={(e) => setCalcFilters({...calcFilters, fecha: e.target.value})} data-testid="filter-calc-fecha" /></TableHead>
              <TableHead className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={calcFilters.total} onChange={(e) => setCalcFilters({...calcFilters, total: e.target.value})} data-testid="filter-calc-total" /></TableHead>
              <TableHead className="px-2 py-1.5">
                <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={calcFilters.estatus_pago} onChange={(e) => setCalcFilters({...calcFilters, estatus_pago: e.target.value})} data-testid="filter-calc-estatus-pago">
                  <option value="todos">Todos</option><option value="Sin pagar">Sin pagar</option><option value="Pagado">Pagado</option>
                </select>
              </TableHead>
              <TableHead className="px-2 py-1.5">
                <select className="h-7 w-full text-xs border rounded-md px-1 bg-white text-slate-700" value={calcFilters.estatus} onChange={(e) => setCalcFilters({...calcFilters, estatus: e.target.value})} data-testid="filter-calc-estatus">
                  <option value="todos">Todos</option><option value="Registrado">Registrado</option><option value="Enviado a Caja">Enviado a Caja</option><option value="Anulado">Anulado</option>
                </select>
              </TableHead>
              <TableHead className="px-2 py-1.5"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCalculos.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">No hay cálculos registrados</TableCell></TableRow>
            ) : filteredCalculos.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-sm">{item.nro_boletin_vehiculo}</TableCell>
                <TableCell className="font-mono text-sm">{item.rif}</TableCell>
                <TableCell className="text-sm">{item.nombre_razon_social || "-"}</TableCell>
                <TableCell className="text-sm font-medium">{item.placa || "-"}</TableCell>
                <TableCell className="text-center">
                  <div className="flex flex-wrap justify-center gap-1">
                    {(item.anios_pagados && item.anios_pagados.length > 0 ? item.anios_pagados : [item.anio_a_pagar]).filter(Boolean).map(a => (
                      <span key={a} className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">{a}</span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-sm">{item.fecha || "-"}</TableCell>
                <TableCell className="text-sm text-right font-medium">{(item.total || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</TableCell>
                <TableCell className="text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.estatus_pago === "Pagado" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {item.estatus_pago || "Sin pagar"}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.estatus === "Enviado a Caja" ? "bg-blue-100 text-blue-700" : item.estatus === "Anulado" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
                    {item.estatus || "Registrado"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {item.estatus === "Registrado" && (
                      <>
                        <Button variant="ghost" size="sm" className="text-xs text-blue-600" onClick={() => handleEnviarCajaClick(item)} data-testid={`btn-enviar-caja-veh-${item.id}`}>Enviar a Caja</Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditCalculo(item)} data-testid={`btn-edit-calveh-${item.id}`}><Pencil className="w-4 h-4 text-blue-600" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteCalculo(item.id)} data-testid={`btn-delete-calveh-${item.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </>
                    )}
                    {item.estatus === "Enviado a Caja" && item.estatus_pago !== "Pagado" && (
                      <Button variant="ghost" size="sm" className="text-xs text-red-600" onClick={() => handleAnularCalculo(item.id)} data-testid={`btn-anular-calveh-${item.id}`}>Anular</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Form Dialog - Cálculo de Vehículo */}
      <Dialog open={calculoDialogOpen} onOpenChange={setCalculoDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              {editingCalculo ? "Editar Cálculo de Vehículo" : "Nuevo Cálculo de Vehículo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={calculoForm.fecha || ""} onChange={(e) => setCalculoForm({ ...calculoForm, fecha: e.target.value })} data-testid="form-fecha-calveh" />
              </div>
              <div className="space-y-2">
                <Label>Oficina</Label>
                <Select value={calculoForm.oficina || ""} onValueChange={(v) => setCalculoForm({ ...calculoForm, oficina: v })}>
                  <SelectTrigger data-testid="form-oficina-calveh"><SelectValue placeholder="Seleccione oficina" /></SelectTrigger>
                  <SelectContent>
                    {oficinas.map((o) => <SelectItem key={o.id || o.nombre} value={o.nombre}>{o.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>RIF / Contribuyente *</Label>
                <Popover open={rifPopoverOpen} onOpenChange={setRifPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between h-10 font-normal text-left" data-testid="form-rif-calveh">
                      {calculoForm.rif ? `${calculoForm.rif} - ${calculoForm.nombre_razon_social || ""}` : <span className="text-slate-500">Buscar contribuyente...</span>}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[500px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar por RIF o nombre..." />
                      <CommandList>
                        <CommandEmpty>No se encontraron contribuyentes con vehículos registrados</CommandEmpty>
                        <CommandGroup>
                          {contribConRegistro.map(c => (
                            <CommandItem key={c.rif} value={`${c.rif} ${c.nombre_razon_social || ""}`} onSelect={() => selectContribCalculo(c)}>
                              <Check className={`mr-2 h-4 w-4 ${calculoForm.rif === c.rif ? "opacity-100" : "opacity-0"}`} />
                              <span className="font-mono text-xs">{c.rif}</span>
                              <span className="ml-2 text-sm text-slate-600 truncate">{c.nombre_razon_social || ""}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Nombre / Razón Social</Label>
                <Input value={calculoForm.nombre_razon_social || ""} readOnly className="bg-slate-50" data-testid="form-nombre-calveh" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Placa *</Label>
                <Popover open={placaPopoverOpen} onOpenChange={setPlacaPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between h-10 font-normal text-left" data-testid="form-placa-calveh">
                      {calculoForm.placa || <span className="text-slate-500">Seleccionar placa...</span>}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Buscar placa..." />
                      <CommandList>
                        <CommandEmpty>No hay vehículos registrados para este contribuyente</CommandEmpty>
                        <CommandGroup>
                          {vehiculosContrib.map(v => (
                            <CommandItem key={v.id} value={`${v.placa} ${v.marca || ""} ${v.modelo || ""}`} onSelect={() => selectPlaca(v)}>
                              <Check className={`mr-2 h-4 w-4 ${calculoForm.placa === v.placa ? "opacity-100" : "opacity-0"}`} />
                              <span className="font-mono text-sm font-medium">{v.placa}</span>
                              <span className="ml-2 text-xs text-slate-500">{v.marca} {v.modelo} {v.anio} - {v.tipo_clasificacion || ""}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Últ. Año Pago</Label>
                <Input type="number" value={calculoForm.ultimo_anio_pago ?? ""} readOnly className="bg-slate-50" data-testid="form-ult-anio-calveh" />
              </div>
            </div>

            {/* Años a Pagar - Checkboxes */}
            {!editingCalculo && calculoForm.placa && (
              <div className="border rounded-lg p-4 space-y-3">
                <Label className="text-base font-semibold">Años a Pagar</Label>
                {aniosPendientes.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2">No hay años pendientes de pago para este vehículo.</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {aniosPendientes.map((anio) => {
                      const isSelected = aniosSeleccionados.includes(anio);
                      return (
                        <label
                          key={anio}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all select-none ${
                            isSelected ? "bg-blue-50 border-blue-500 text-blue-800 font-bold" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-blue-50/50"
                          }`}
                          data-testid={`check-anio-${anio}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAnio(anio)}
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">{anio}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {aniosSeleccionados.length > 0 && (
                  <p className="text-xs text-blue-600 font-medium">Se creará 1 cálculo con {aniosSeleccionados.length} año(s): {aniosSeleccionados.join(", ")}</p>
                )}
              </div>
            )}

            {editingCalculo && (
              <div className="flex items-center gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Año a Pagar</Label>
                  <div className="h-10 flex items-center justify-center px-6 bg-green-50 border border-green-200 rounded-md text-sm font-bold text-green-800" data-testid="form-anio-a-pagar-calveh">
                    {editingCalculo.anio_a_pagar || "-"}
                  </div>
                </div>
              </div>
            )}

            {/* Líneas de Impuestos/Tasas */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Líneas de Impuestos/Tasas</Label>
                <Button variant="outline" size="sm" onClick={addLinea} data-testid="btn-add-linea-calveh">
                  <Plus className="w-3 h-3 mr-1" /> Agregar Línea
                </Button>
              </div>
              {(calculoForm.lineas_impuestos || []).length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No hay líneas. Haga clic en "Agregar Línea".</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="px-2 py-1 text-left font-medium text-slate-600">Impuestos/Tasas</th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">Ingreso Bruto</th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">Importe</th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">Sub Total</th>
                        <th className="px-2 py-1 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(calculoForm.lineas_impuestos || []).map((linea, idx) => (
                        <tr key={idx} className="border-t" data-testid="linea-impuesto-row" data-tipo-nombre={linea.tipo || linea.tipo_impuesto_nombre || linea.concepto || ""} data-anclado={linea._anclado ? "true" : "false"}>
                          <td className="px-2 py-1">
                            {linea.auto_generada ? (
                              <div className="h-8 flex items-center text-xs font-medium text-slate-700 bg-slate-50 px-2 rounded border" data-testid={`linea-tipo-${idx}`}>{linea.tipo}</div>
                            ) : (
                              <Select value={linea.tipo || ""} onValueChange={(v) => updateLinea(idx, "tipo", v)}>
                                <SelectTrigger className="h-8 text-xs" data-testid={`linea-tipo-${idx}`}><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                                <SelectContent>
                                  {clasificadorVeh.map(c => (
                                    <SelectItem key={c.id} value={c.tipo_clase_vehiculo}>{c.tipo_clase_vehiculo}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" step="0.01" readOnly={!!linea.auto_generada} className={`h-8 text-xs text-right ${linea.auto_generada ? "bg-slate-50" : ""}`} value={linea.ingreso_bruto || ""} onChange={(e) => updateLinea(idx, "ingreso_bruto", parseFloat(e.target.value) || 0)} data-testid={`linea-ingreso-${idx}`} />
                          </td>
                          <td className="px-2 py-1">
                            <Input type="number" step="0.01" className={`h-8 text-xs text-right ${linea.auto_generada ? "bg-slate-50" : ""}`} value={linea.importe || ""} readOnly={!!linea.auto_generada} onChange={(e) => updateLinea(idx, "importe", parseFloat(e.target.value) || 0)} data-testid={`linea-importe-${idx}`} />
                          </td>
                          <td className="px-2 py-1 text-right font-medium text-sm">{(linea.sub_total || 0).toLocaleString("es-VE", { minimumFractionDigits: 2 })}</td>
                          <td className="px-2 py-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLinea(idx)} data-testid={`linea-remove-${idx}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t bg-slate-50">
                        <td colSpan={3} className="px-2 py-1 text-right font-semibold text-sm">Sub Total Líneas:</td>
                        <td className="px-2 py-1 text-right font-bold text-sm">{calcSubTotal().toLocaleString("es-VE", { minimumFractionDigits: 2 })}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Campos finales */}
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Descuento (Bs.)</Label>
                <Input type="number" step="0.01" className="h-8 text-sm text-right" value={calculoForm.descuento ?? ""} onChange={(e) => setCalculoForm({ ...calculoForm, descuento: parseFloat(e.target.value) || 0 })} data-testid="form-descuento-calveh" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Reintegro</Label>
                <Input type="number" step="0.01" className="h-8 text-sm text-right" value={calculoForm.reintegro ?? ""} onChange={(e) => setCalculoForm({ ...calculoForm, reintegro: parseFloat(e.target.value) || 0 })} data-testid="form-reintegro-calveh" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Retención (Bs.)</Label>
                <Input type="number" step="0.01" className="h-8 text-sm text-right" value={calculoForm.retencion ?? ""} onChange={(e) => setCalculoForm({ ...calculoForm, retencion: parseFloat(e.target.value) || 0 })} data-testid="form-retencion-calveh" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Multa (Bs.)</Label>
                <Input type="number" step="0.01" className="h-8 text-sm text-right" value={calculoForm.multa ?? ""} onChange={(e) => setCalculoForm({ ...calculoForm, multa: parseFloat(e.target.value) || 0 })} data-testid="form-multa-calveh" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Divisas ($)</Label>
                <div className="h-8 flex items-center justify-end px-3 bg-slate-50 border rounded-md text-sm text-slate-700" data-testid="form-divisas-usd-calveh">
                  {cotizacionActiva?.dolar > 0 ? (calcTotal() / cotizacionActiva.dolar).toFixed(2) : "0.00"}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Divisas (E)</Label>
                <div className="h-8 flex items-center justify-end px-3 bg-slate-50 border rounded-md text-sm text-slate-700" data-testid="form-divisas-eur-calveh">
                  {cotizacionActiva?.euro > 0 ? (calcTotal() / cotizacionActiva.euro).toFixed(2) : "0.00"}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Total (Bs.)</Label>
                <div className="h-8 flex items-center justify-end px-3 bg-blue-50 border border-blue-200 rounded-md text-sm font-bold text-blue-800" data-testid="form-total-calveh">
                  {calcTotal().toLocaleString("es-VE", { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalculoDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveCalculo} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar-calculo-veh">{editingCalculo ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogoFechaCaja
        open={fechaCajaDialogOpen}
        onOpenChange={setFechaCajaDialogOpen}
        fechaCalculo={calculoParaEnviar?.fecha}
        onConfirm={() => {
          setFechaCajaDialogOpen(false);
          handleEnviarCaja(calculoParaEnviar.id, true);
          setCalculoParaEnviar(null);
        }}
        onCancel={() => {
          setFechaCajaDialogOpen(false);
          setCalculoParaEnviar(null);
        }}
      />
    </div>
  );
};

export default VehiculosCalculo;
